import { motion } from 'framer-motion';

/**
 * EngagedlyRoughSketch, a live comparison card that lays out the two
 * workshop teams' rough sketches side-by-side. The card frame, headers,
 * badges and browser chrome are designed; the "layout" area itself shows
 * a cropped portion of the original whiteboard photo so viewers see the
 * authentic sketch inside a polished frame.
 *
 *   • Team A (orange), a widget-driven layout.
 *   • Team B (green), a resource-centre + announcement-strip pattern.
 */

const easeOut = [0.2, 0.8, 0.2, 1];

// Per-team wireframe images uploaded by the site owner. Each is a clean
// wireframe of that team's sketch, no cropping required.
const TEAM_A_SKETCH =
    'https://customer-assets-cm19k8pv.emergentagent.net/job_himanshu-redesign/artifacts/jxw9fjvo_image.png';
const TEAM_B_SKETCH =
    'https://customer-assets-cm19k8pv.emergentagent.net/job_himanshu-redesign/artifacts/650o1biq_image.webp';

// A single team's mockup card, schematic browser frame + a cropped view
// of the original whiteboard sketch for that team.
const TeamCard = ({ team }) => (
    <motion.article
        initial={{ opacity: 0, y: 14 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: '-6%' }}
        transition={{ duration: 0.55, ease: easeOut, delay: team.delay }}
        data-testid={`team-${team.key}-card`}
        className="relative rounded-2xl border p-5 md:p-6 flex flex-col gap-4 overflow-hidden"
        style={{
            background: `${team.accent}0F`,
            borderColor: `${team.accent}55`,
        }}
    >
        {/* Colour edge */}
        <span
            aria-hidden
            className="absolute top-0 left-0 right-0 h-[3px]"
            style={{ background: team.accent }}
        />

        {/* Team header */}
        <header className="flex items-start justify-between gap-3">
            <div>
                <div
                    className="font-mono-brand text-[10px] uppercase tracking-[0.22em] mb-1"
                    style={{ color: team.accent }}
                >
                    {team.eyebrow}
                </div>
                <h4 className="font-display text-2xl md:text-[26px] leading-[1.05] text-[var(--ink)]">
                    {team.name}
                    <span className="text-[var(--accent)]">.</span>
                </h4>
                <p className="mt-1.5 font-sans-brand text-[12.5px] leading-[1.5] text-[var(--ink-2)]">
                    {team.thesis}
                </p>
            </div>
            <span
                className="inline-flex items-center justify-center w-9 h-9 rounded-full font-display text-[15px] shrink-0"
                style={{
                    background: team.accent,
                    color: '#0B0B0C',
                }}
            >
                {team.letter}
            </span>
        </header>

        {/* Browser mockup frame, chrome + cropped original whiteboard photo */}
        <div
            className="relative rounded-xl border overflow-hidden"
            style={{
                borderColor: `${team.accent}66`,
                background: `${team.accent}08`,
            }}
        >
            {/* Fake browser chrome */}
            <div
                className="flex items-center gap-1.5 px-3 py-2 border-b"
                style={{ borderColor: `${team.accent}33` }}
            >
                <span className="w-1.5 h-1.5 rounded-full bg-[var(--ink-3)] opacity-60" />
                <span className="w-1.5 h-1.5 rounded-full bg-[var(--ink-3)] opacity-60" />
                <span className="w-1.5 h-1.5 rounded-full bg-[var(--ink-3)] opacity-60" />
                <span
                    className="ml-2 font-mono-brand text-[9.5px] uppercase tracking-[0.18em]"
                    style={{ color: team.accent }}
                >
                    engagedly.com &nbsp;·&nbsp; home &nbsp;·&nbsp; {team.name} rough sketch
                </span>
            </div>

            {/* Full per-team wireframe image, clean single upload, no crop */}
            <div className="relative w-full overflow-hidden bg-white">
                <img
                    src={team.sketch}
                    alt={`${team.name} rough sketch, ${team.eyebrow}`}
                    loading="lazy"
                    className="block w-full h-auto"
                />
                {/* Subtle inner accent glow */}
                <div
                    aria-hidden
                    className="absolute inset-0 pointer-events-none"
                    style={{
                        boxShadow: `inset 0 0 60px ${team.accent}22`,
                    }}
                />
            </div>
        </div>

        {/* Footer count chip */}
        <div className="flex items-center gap-2">
            <span
                className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full font-mono-brand text-[10px] uppercase tracking-[0.18em]"
                style={{
                    background: `${team.accent}14`,
                    color: team.accent,
                    border: `1px solid ${team.accent}55`,
                }}
            >
                <span
                    className="w-1.5 h-1.5 rounded-full"
                    style={{ background: team.accent }}
                />
                Original workshop sketch
            </span>
        </div>
    </motion.article>
);

const teams = [
    {
        key: 'a',
        letter: 'A',
        name: 'Team A',
        eyebrow: 'Widget-driven layout',
        thesis:
            'A dense, opinionated dashboard. Every widget is visible; the user chooses what to engage with. Attention · Org updates · Team outing · Try it out · Launch/Promotions · Graphical Representation · Live Vote · Suggestions.',
        accent: '#f97316',       // orange-500
        delay: 0,
        sketch: TEAM_A_SKETCH,
    },
    {
        key: 'b',
        letter: 'B',
        name: 'Team B',
        eyebrow: 'Resource-centre pattern',
        thesis:
            'A calmer, guided homepage. Anchored by an announcement strip and a resource centre; extras earn their place. Resource Center + Announcements strip + Fun event carousel + Onboarding checklist + Manage / Add widgets.',
        accent: '#10b981',       // emerald-500
        delay: 0.08,
        sketch: TEAM_B_SKETCH,
    },
];

export const EngagedlyRoughSketch = () => (
    <motion.figure
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: '-6%' }}
        transition={{ duration: 0.6, ease: easeOut }}
        data-testid="engagedly-rough-sketch"
        className="mb-6 md:mb-8 rounded-2xl border border-[var(--line)] bg-[var(--surface)] overflow-hidden"
    >
        <div className="p-4 md:p-6 grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5">
            {teams.map((team) => (
                <TeamCard key={team.key} team={team} />
            ))}
        </div>
    </motion.figure>
);

export default EngagedlyRoughSketch;
